import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import { MenuGroup } from '@atlaskit/menu';
import { Popup } from '@atlaskit/popup';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { type Shortcuts } from '@databases/keyboard-shortcuts';
import { registerKeyboardShortcut } from '@databases/react';
import useHandleOutsideClick from '@databases/utils/hooks/useHandleOutsideClick';
import React, {
  useState,
  useRef,
  useMemo,
  useCallback,
  useEffect,
  useId,
  type ReactNode,
} from 'react';
import { useIntl } from 'react-intl-next';

import { ActionComponent } from './ActionComponent';
import { messages } from './messages';

const actionItemStyles = xcss({
  background: token('color.background.neutral.bold', '#292A2E'),
  border: `${token('border.width', '1px')} solid ${token('color.border')}`,
  boxShadow: 'elevation.shadow.overflow',
  color: 'color.text.inverse',
  position: 'absolute',
  bottom: 'space.150',
  left: '0',
  borderRadius: token('radius.medium', '6px'),
  paddingBlock: 'space.100',
});

const popupContainerStyles = xcss({
  position: 'relative',
});

export type Action = {
  type:
    | 'move-up'
    | 'move-down'
    | 'move-left'
    | 'move-right'
    | 'add-above'
    | 'add-below'
    | 'delete-entry'
    | 'delete-field'
    | 'delete';
  message: string;
  icon: ReactNode;
  onClick: () => void;
  shouldRender: boolean;
  shortcut?: Shortcuts;
};

export type Trigger = {
  type: 'move' | 'add' | 'delete';
  message: string;
  icon: ReactNode;
  selectionMode: 'entry' | 'field' | 'value';
};

type ActionPopupProps = {
  actions: Action[];
  trigger: Trigger;
};

export const ActionPopup = ({ actions, trigger }: ActionPopupProps) => {
  const { formatMessage } = useIntl();
  const popupId = useId();
  const popoverRef = useRef(null);
  const triggerRef = useRef(null);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSelected, setIsSelected] = useState(false);

  const handleToggleMenu = useCallback(() => {
    setIsMenuOpen(!isMenuOpen);
    setIsSelected(!isSelected);
  }, [isMenuOpen, isSelected]);

  const handleCloseMenu = useCallback(() => {
    if (isMenuOpen) {
      setIsMenuOpen(false);
      setIsSelected(false);
    }
  }, [isMenuOpen]);

  useHandleOutsideClick(
    handleCloseMenu,
    popoverRef,
    [`[id="action-popup-trigger-${popupId}"]`],
    isMenuOpen
  );

  const availableActions = useMemo(
    () => actions.filter((action) => action.shouldRender),
    [actions]
  );

  // Register keyboard shortcuts for all available actions when there are multiple actions
  // Single action shortcuts will be handled directly in the action component
  // This ensures shortcuts work even when the popup is closed
  useEffect(() => {
    if (availableActions.length <= 1) {
      return;
    }
    const unregisterFunctions = availableActions.flatMap((action) =>
      action.shortcut
        ? [registerKeyboardShortcut(action.shortcut.keyboardShortCut, action.onClick)]
        : []
    );

    return () => {
      unregisterFunctions.forEach((unregister) => unregister());
    };
  }, [availableActions]);

  const popupTrigger = useMemo(
    () => (
      <Box ref={triggerRef} id={`action-popup-trigger-${popupId}`}>
        <ActionComponent
          isTriggerAction
          type={trigger.type}
          selectionMode={trigger.selectionMode}
          message={trigger.message}
          iconBefore={trigger.icon}
          iconAfter={
            <ChevronDownIcon
              label={formatMessage(messages['action-bar.chevron.down.icon.label'])}
              size="small"
            />
          }
          onClick={handleToggleMenu}
          isSelected={isSelected}
        />
      </Box>
    ),
    [
      popupId,
      trigger.type,
      trigger.selectionMode,
      trigger.message,
      trigger.icon,
      formatMessage,
      handleToggleMenu,
      isSelected,
    ]
  );

  const popupActions = useMemo(
    () => (
      <Box xcss={actionItemStyles} ref={popoverRef}>
        <MenuGroup>
          {availableActions.map((action, index) => (
            <Box key={`${action.type}-${index}`}>
              <ActionComponent
                type={action.type}
                selectionMode={trigger.selectionMode}
                message={action.message}
                iconBefore={action.icon}
                onClick={action.onClick}
                isPopupAction
                shortcut={action.shortcut}
              />
            </Box>
          ))}
        </MenuGroup>
      </Box>
    ),
    [availableActions, trigger.selectionMode]
  );

  if (availableActions.length === 0) {
    return null;
  }

  if (availableActions.length === 1) {
    const action = availableActions[0];
    return (
      <ActionComponent
        type={action.type}
        selectionMode={trigger.selectionMode}
        message={action.message}
        iconBefore={action.icon}
        onClick={action.onClick}
        shortcut={action.shortcut}
      />
    );
  }

  return (
    <Box xcss={popupContainerStyles}>
      <Popup
        shouldRenderToParent
        placement="top"
        isOpen={isMenuOpen}
        strategy="absolute"
        content={() => popupActions}
        trigger={() => popupTrigger}
      />
    </Box>
  );
};
